import FormattedMessage from 'I18n/components/FormattedMessage';
import { BreezeSingleStarIcon } from 'foundations-assets/icons/components/BreezeSingleStarIcon';
import { DataSyncIcon } from 'foundations-assets/icons/components/DataSyncIcon';
import { DataSyncLeftIcon } from 'foundations-assets/icons/components/DataSyncLeftIcon';
import { DataSyncRightIcon } from 'foundations-assets/icons/components/DataSyncRightIcon';
import { DownIcon } from 'foundations-assets/icons/components/DownIcon';
import { SprocketIcon } from 'foundations-assets/icons/components/SprocketIcon';
import { UpIcon } from 'foundations-assets/icons/components/UpIcon';
import { Flex } from 'foundations-components/components/Flex';
import { Spacer } from 'foundations-components/components/Spacer';
import { Text } from 'foundations-components/components/Text';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Image } from 'foundations-components/transitional/components/Image';
import { Small } from 'foundations-components/transitional/components/Small';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { MediaQueryView } from 'marketplace-ui-common/layout/components/MediaQueryView';
import { Fragment, useMemo, useState } from 'react';
import { useDetailsPageContext } from 'marketplace-ui-common/context/DetailsPageContext';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import unescapedText from 'I18n/utils/unescapedText';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function getHubspotObjectName(objectType, objectName) {
  switch (objectType) {
    case 'APP_OBJECT':
      return objectName;
    case 'BREEZE_ASSISTANT':
    case 'DATA_STUDIO':
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: `detailsPage.functionalityCard.tabs.sharedData.data.${objectType}`
      });
    default:
      return objectName ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: `detailsPage.functionalityCard.tabs.sharedData.data.${objectName}`
      }) : '';
  }
}
export function SharedDataTableRow({
  groupedSharedObject,
  index,
  onTrackInteraction = () => {}
}) {
  const {
    iconUrl,
    name
  } = useDetailsPageContext();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const {
    sharedObjectDirection,
    sharedAppObject,
    hubspotObjects,
    sharedHubspotObjectType,
    sharedObjectDescription,
    customFieldMappings,
    defaultFieldMappings
  } = groupedSharedObject;
  const renderedSyncIcon = useMemo(() => {
    if (sharedObjectDirection === 'INBOUND') {
      return DataSyncRightIcon;
    } else if (sharedObjectDirection === 'OUTBOUND') {
      return DataSyncLeftIcon;
    } else {
      return DataSyncIcon;
    }
  }, [sharedObjectDirection]);
  const caretIcon = useMemo(() => {
    return dropdownOpen ? UpIcon : DownIcon;
  }, [dropdownOpen]);
  const hasFieldMappings = useMemo(() => {
    return ((customFieldMappings === null || customFieldMappings === void 0 ? void 0 : customFieldMappings.length) || 0) > 0 || ((defaultFieldMappings === null || defaultFieldMappings === void 0 ? void 0 : defaultFieldMappings.length) || 0) > 0;
  }, [customFieldMappings, defaultFieldMappings]);
  const renderedFieldMappings = useMemo(() => {
    return ['DEFAULT', 'CUSTOM'].map(fieldType => {
      const isDefaultMapping = fieldType === 'DEFAULT';
      const mappings = isDefaultMapping ? defaultFieldMappings : customFieldMappings;
      if (!mappings || !mappings.length) {
        return null;
      }
      return /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx("tr", {
          "data-test-id": "field-mapping-dropdown",
          children: /*#__PURE__*/_jsx("td", {
            colSpan: 3,
            children: /*#__PURE__*/_jsx(Text, {
              typography: "body-125",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: `detailsPage.functionalityCard.tabs.sharedData.fieldMappings.${fieldType}`
              })
            })
          })
        }), mappings.map((mapping, mappingIndex) => {
          const appField = isDefaultMapping ? mapping.defaultFieldMappingYourApp : mapping;
          const hubSpotField = isDefaultMapping ? mapping.defaultFieldMappingHubspot : null;
          return /*#__PURE__*/_jsxs("tr", {
            "data-test-id": "field-mapping",
            children: [/*#__PURE__*/_jsx("td", {
              colSpan: isDefaultMapping ? 1 : 3,
              children: /*#__PURE__*/_jsx(Text, {
                as: "small",
                children: appField
              })
            }), isDefaultMapping && /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx("td", {}), /*#__PURE__*/_jsx("td", {
                children: /*#__PURE__*/_jsx(Flex, {
                  paddingInlineStart: "1500",
                  children: /*#__PURE__*/_jsx(Text, {
                    as: "small",
                    children: hubSpotField
                  })
                })
              })]
            })]
          }, `${fieldType.toLowerCase()}-mapping-${mappingIndex}`);
        })]
      }, fieldType);
    });
  }, [customFieldMappings, defaultFieldMappings]);
  const objectsCellContent = useMemo(() => {
    return hubspotObjects === null || hubspotObjects === void 0 ? void 0 : hubspotObjects.map((objectName, objectIndex) => {
      const objectType = sharedHubspotObjectType[objectIndex];
      let renderedIcon;
      switch (objectType) {
        case 'BREEZE_ASSISTANT':
          renderedIcon = /*#__PURE__*/_jsx(BreezeSingleStarIcon, {
            color: "fill-accent-magenta-default",
            size: "400"
          });
          break;
        case 'APP_OBJECT':
          renderedIcon = /*#__PURE__*/_jsx(Image, {
            inlineSize: "20px",
            src: iconUrl
          });
          break;
        default:
          renderedIcon = /*#__PURE__*/_jsx(SprocketIcon, {
            color: "fill-brand-default",
            size: "400"
          });
      }
      const hubspotObjectName = getHubspotObjectName(objectType, objectName);
      return /*#__PURE__*/_jsx(Flex, {
        flexDirection: "column",
        children: /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          gap: "200",
          children: [/*#__PURE__*/_jsx(Tooltip, {
            placement: "top",
            disabled: objectType === 'BREEZE_ASSISTANT' || objectType === 'DATA_STUDIO',
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: `detailsPage.functionalityCard.tabs.sharedData.hubspotObjectTooltip.${objectType}`,
              options: {
                name
              }
            }),
            children: renderedIcon
          }), /*#__PURE__*/_jsxs(Flex, {
            flexDirection: "column",
            children: [/*#__PURE__*/_jsx(Text, {
              typography: "heading-50",
              textTransform: "none",
              children: hubspotObjectName
            }), objectType === 'APP_OBJECT' && /*#__PURE__*/_jsx(Small, {
              use: "help",
              children: /*#__PURE__*/_jsxs(Flex, {
                alignItems: "center",
                children: [/*#__PURE__*/_jsx(FormattedMessage, {
                  message: `detailsPage.functionalityCard.tabs.sharedData.type.${objectType}`
                }), /*#__PURE__*/_jsx(Spacer, {
                  space: "100",
                  display: "inline-block"
                }), /*#__PURE__*/_jsx(HelpIcon, {
                  title: unescapedText('detailsPage.functionalityCard.tabs.sharedData.appObjectTooltip')
                })]
              })
            })]
          })]
        })
      }, objectIndex);
    });
  }, [hubspotObjects, sharedHubspotObjectType, iconUrl, name]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs("tr", {
      onClick: () => {
        if (hasFieldMappings) {
          setDropdownOpen(!dropdownOpen);
          onTrackInteraction({
            action: dropdownOpen ? 'closed shared data table row' : 'opened shared data table row'
          });
        }
      },
      children: [/*#__PURE__*/_jsx("td", {
        width: "35%",
        children: /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          children: [hasFieldMappings ? /*#__PURE__*/_jsx(IconHolder, {
            borderRadius: "200",
            borderWidth: "200",
            borderColor: "border-core-default",
            borderStyle: "solid",
            color: "icon-core-default",
            icon: caretIcon,
            size: "300",
            padding: "100",
            marginInlineEnd: "400"
          }) : null, iconUrl && /*#__PURE__*/_jsx(Image, {
            inlineSize: "20px",
            src: iconUrl,
            marginInlineEnd: "200"
          }), /*#__PURE__*/_jsx(Text, {
            typography: "heading-50",
            textTransform: "none",
            children: sharedAppObject
          })]
        })
      }), /*#__PURE__*/_jsx("td", {
        width: "25%",
        children: /*#__PURE__*/_jsx(Flex, {
          alignItems: "center",
          justifyContent: "center",
          children: /*#__PURE__*/_jsx(Tooltip, {
            placement: "top",
            title: sharedObjectDescription || /*#__PURE__*/_jsx(FormattedMessage, {
              message: `detailsPage.functionalityCard.tabs.sharedData.sync.${sharedObjectDirection}`
            }),
            children: /*#__PURE__*/_jsx(IconHolder, {
              color: "fill-primary-default",
              icon: renderedSyncIcon,
              size: "600",
              padding: "0"
            })
          })
        })
      }), /*#__PURE__*/_jsxs("td", {
        width: "40%",
        children: [/*#__PURE__*/_jsx(MediaQueryView, {
          min: "xl",
          children: /*#__PURE__*/_jsx(Flex, {
            flexDirection: "column",
            gap: "200",
            paddingInlineStart: "1500",
            children: objectsCellContent
          })
        }), /*#__PURE__*/_jsx(MediaQueryView, {
          max: "xl",
          children: /*#__PURE__*/_jsx(Flex, {
            flexDirection: "column",
            gap: "200",
            children: objectsCellContent
          })
        })]
      })]
    }, index), dropdownOpen && renderedFieldMappings]
  });
}